import React from 'react';
import { Tag, Tooltip } from 'antd';

import { listAllSales } from '../../utils/webApi';
import { IDiagnosticItem, IDiagnosticResendResponse } from '../../models/sales-sf-diagnostic.models';

/**
 * O reenvio responde antes de o pedido terminar de ser processado (a criação no
 * Salesforce passa por fila), então a listagem precisa ser recarregada depois.
 * Em vez de uma pausa fixa, consultamos a própria tabela de Vendas SF até o
 * pedido aparecer: recarrega assim que ele existe, e desiste no teto.
 */
export const RESEND_POLL_TIMEOUT_MS = 5000;
export const RESEND_POLL_INTERVAL_MS = 400;

/**
 * pageSize mínimo aceito por GET /orders do febracis-invoices (SchemaListing).
 * Pedir menos que isso devolve 400.
 */
const SALES_MIN_PAGE_SIZE = 5;

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * O pedido da linha reenviada já apareceu na tabela de Vendas SF?
 *
 * Não basta achar o orderId: numa assinatura todas as parcelas compartilham o mesmo
 * orderId, e o pedido de outra parcela encerraria a espera cedo demais. Por isso a
 * linha só conta quando o paymentId dela contém o identificador desta compra.
 */
export const matchesItem = (salePaymentId: string, item: IDiagnosticItem): boolean => {
    const tokens = String(salePaymentId || '').split(/\s*[:|]\s*/).map(e => e.trim()).filter(Boolean);
    return tokens.some(token => token === item.paymentId || token === item.transactionId);
};

/**
 * Espera o pedido aparecer na tabela de Vendas SF, com teto de timeoutMs. Nunca
 * lança: falha de consulta só encurta a espera, porque quem decide o que exibir é
 * o reload do diagnóstico que vem depois.
 */
export const waitForOrder = async (
    item: IDiagnosticItem,
    response: IDiagnosticResendResponse | null | undefined,
    { intervalMs = RESEND_POLL_INTERVAL_MS, timeoutMs = RESEND_POLL_TIMEOUT_MS } = {}
): Promise<void> => {
    const search = response && response.orderId;

    if (!search) {
        await wait(intervalMs);
        return;
    }

    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
        await wait(intervalMs);

        try {
            const sales = await listAllSales({ page: 1, pageSize: SALES_MIN_PAGE_SIZE, search });
            const found = ((sales && sales.data) || []).some(sale => matchesItem(sale.paymentId, item));

            if (found) {
                return;
            }
        } catch (error) {
            // Consulta de espera não é o resultado do reenvio: segue para o reload.
            return;
        }
    }
};

const SITUATION_LABEL: { [key: string]: string } = {
    PENDING: 'Pendente',
    NAO_INTEGRADO: 'Não integrado',
};

const CATEGORY_LABEL: { [key: string]: string } = {
    ERRO: 'Erro',
    NAO_IMPLEMENTADO: 'Falta implementação',
};

/** Valor já em reais (o contrato do diagnóstico não usa centavos). */
export const formatToBRL = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '-';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(Number(amount));
};

const FIELD_LABEL: { [path: string]: string } = {
    type: 'Tipo de pessoa',
    firstName: 'Nome',
    lastName: 'Sobrenome',
    corporateName: 'Razão social',
    doingBusiness: 'Nome fantasia',
    email: 'E-mail',
    phone: 'Telefone',
    originCountry: 'País de origem',
    'document.number': 'Número do documento',
    'document.type': 'Tipo do documento',
    'address.typeAddress': 'Tipo de logradouro',
    'address.street': 'Rua',
    'address.number': 'Número',
    'address.complement': 'Complemento',
    'address.district': 'Bairro',
    'address.city': 'Cidade',
    'address.state': 'UF',
    'address.country': 'País',
    'address.postalCode': 'CEP',
};

/**
 * Tradução do Motivo por padrão de texto. Cada entrada casa a parte textual da
 * mensagem e o `template` reaproveita os grupos capturados ($1, $2...), de modo
 * que IDs, números e nomes de campo são copiados do original — nunca fixos aqui.
 * A ordem importa: a primeira que casar vence.
 */
const SF_TRANSLATIONS: { regex: RegExp, template: string }[] = [
    {
        // Duração de licença sem produto cadastrado na tabela SalesforceInfo.
        regex: /items\[\d+\]\.productCode is required/i,
        template: 'Produto não cadastrado no Salesforce para esta duração de licença',
    },
    {
        // Cadastrado, mas com um código que não existe no Salesforce.
        regex: /productCode (\S+) not found/i,
        template: 'Produto $1 não existe no Salesforce — corrija o cadastro desta duração de licença',
    },
    {
        regex: /payments\[\d+\]\.type:\s*(\S+) is not a valid type/i,
        template: 'Meio de pagamento $1 não é aceito pelo Salesforce',
    },
    {
        regex: /Duplicate external id specified:\s*(\S+)/i,
        template: 'Pedido já existe no Salesforce com o mesmo ID externo $1',
    },
    {
        regex: /Attempting to update \(as part of an upsert\) parent field (\w+) with new value (\S+?),?\s/i,
        template: 'Conflito de upsert no Salesforce: o campo $1 já aponta para outro registro (novo valor $2)',
    },
    {
        regex: /\bUNABLE_TO_LOCK_ROW\b/,
        template: 'Registro bloqueado no Salesforce — reenvie em instantes',
    },
    {
        regex: /\bREQUIRED_FIELD_MISSING\b/,
        template: 'Campo obrigatório ausente no Salesforce',
    },
    {
        regex: /\bFIELD_CUSTOM_VALIDATION_EXCEPTION\b/,
        template: 'Regra de validação do Salesforce rejeitou o pedido',
    },
    {
        regex: /\bCANNOT_INSERT_UPDATE_ACTIVATE_ENTITY\b/,
        template: 'Automação (trigger) do Salesforce falhou ao processar o pedido',
    },
    {
        regex: /\bINVALID_CROSS_REFERENCE_KEY\b/,
        template: 'Referência inválida entre registros no Salesforce',
    },
    {
        regex: /\bDUPLICATE_VALUE\b/,
        template: 'Registro duplicado no Salesforce',
    },
    {
        regex: /\bSTRING_TOO_LONG\b/,
        template: 'Valor acima do limite de um campo no Salesforce',
    },
    {
        regex: /\bMALFORMED_ID\b/,
        template: 'ID malformado enviado ao Salesforce',
    },
    {
        regex: /\bINVALID_FIELD\b/,
        template: 'Campo inexistente ou inválido no Salesforce',
    },
    {
        // Fallback para qualquer outro código: mantém o código como veio.
        regex: /first error:\s*([A-Z_0-9]+)/,
        template: 'Erro retornado pelo Salesforce ($1)',
    },
];

/** Identificador do registro citado na mensagem, copiado como veio. */
const SF_RECORD_ID = /\bwith id (\S+?)[;,\s]/;

const MAX_SUMMARY = 140;

export interface IReasonView {
    /** Frase curta em pt-BR para exibição. */
    summary: string,
    /** Texto original completo, presente quando difere do resumo. */
    details?: string,
}

/**
 * `customer.document.number is required` ou `customer.document.number: X is not a
 * valid cnpj` → campo pt-BR; null quando o trecho não é erro Joi.
 */
const parseJoiSegment = (segment: string): { field: string, required: boolean } | null => {
    const match = segment.match(/^"?customer\.([\w.]+)"?\s*:?\s+(.+)$/);
    if (!match) return null;
    const predicate = match[2].toLowerCase();
    return {
        field: FIELD_LABEL[match[1]] || match[1],
        required: predicate.indexOf('is required') !== -1 || predicate.indexOf('not allowed to be empty') !== -1,
    };
};

/**
 * Converte o `reason` cru do backend (erros Joi concatenados por ' | ', com
 * repetições, ou exceções do Salesforce) em um resumo legível, preservando o
 * texto original em `details`.
 */
export const parseReason = (reason: string | null | undefined): IReasonView => {
    const raw = (reason || '').trim();
    if (!raw) return { summary: '-' };

    const segments = raw.split('|').map(s => s.trim()).filter(Boolean)
        .filter((s, i, all) => all.indexOf(s) === i);
    const joi = segments.map(parseJoiSegment);

    // Motivo misto (campos do comprador + outro erro): traduz os campos e mantém
    // o resto, em vez de devolver tudo cru.
    const outros = segments.filter((_, i) => joi[i] === null);

    if (segments.length > 0 && joi.some(j => j !== null) && outros.length > 0) {
        const campos = joi.filter(j => j !== null).map(j => j!.field);
        const unicos = campos.filter((f, i) => campos.indexOf(f) === i).join(', ');
        const traduzidos = outros.map(trecho => {
            const encontrado = SF_TRANSLATIONS.find(({ regex }) => regex.test(trecho));
            if (!encontrado) return trecho;
            const captura = trecho.match(encontrado.regex);
            return encontrado.template.replace(/\$(\d)/g, (_, i) => (captura && captura[Number(i)]) || '');
        });

        return {
            summary: `Campos do comprador: ${unicos} · ${traduzidos.join(' · ')}`,
            details: raw,
        };
    }

    if (segments.length > 0 && joi.every(j => j !== null)) {
        const unique = (fields: string[]) => fields.filter((f, i) => fields.indexOf(f) === i).join(', ');
        const required = unique(joi.filter(j => j && j.required).map(j => j!.field));
        const invalid = unique(joi.filter(j => j && !j.required).map(j => j!.field));
        const parts: string[] = [];
        if (required) parts.push(`Campos obrigatórios não preenchidos: ${required}`);
        if (invalid) parts.push(`Campos inválidos: ${invalid}`);
        return { summary: parts.join(' · '), details: raw };
    }

    for (const { regex, template } of SF_TRANSLATIONS) {
        const found = raw.match(regex);
        if (!found) continue;

        // $1, $2... saem do próprio texto original: nada de valor fixo na tradução.
        const translated = template.replace(/\$(\d)/g, (_, index) => found[Number(index)] || '');
        const record = raw.match(SF_RECORD_ID);

        return {
            summary: record ? `${translated} — registro ${record[1]}` : translated,
            details: raw,
        };
    }

    if (raw.length > MAX_SUMMARY) {
        return { summary: `${raw.slice(0, MAX_SUMMARY)}…`, details: raw };
    }

    return { summary: raw };
};

/** Resumo com o texto original em tooltip — para a coluna Motivo da lista. */
export const ReasonCell = ({ reason }: { reason: string | null | undefined }) => {
    const { summary, details } = parseReason(reason);
    if (!details) return <>{summary}</>;
    return (
        <Tooltip title={details} placement="topLeft" overlayClassName="diagnostic-reason-tooltip">
            <span className="diagnostic-reason-summary">{summary}</span>
        </Tooltip>
    );
};

/** Resumo com o texto original abaixo — para o Motivo dentro do modal. */
export const ReasonDetail = ({ reason }: { reason: string | null | undefined }) => {
    const { summary, details } = parseReason(reason);
    return (
        <>
            {summary}
            {details && details !== summary && <div className="diagnostic-reason-raw">{details}</div>}
        </>
    );
};

export const SituationTag = ({ situation }: { situation: string }) => (
    <Tag className={`tags-status ${situation === 'PENDING' ? 'diagnostic-pending' : 'diagnostic-nao-integrado'}`}>
        {SITUATION_LABEL[situation] || situation}
    </Tag>
);

export const CategoryTag = ({ category }: { category: string }) => (
    <Tag className={`tags-status ${category === 'ERRO' ? 'diagnostic-erro' : 'diagnostic-nao-implementado'}`}>
        {CATEGORY_LABEL[category] || category}
    </Tag>
);
